import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { makeRequest } from 'Douleutaras/services/apiClient';
import { LOCAL_STORAGE_KEY } from 'Douleutaras/constants/system';
import {
  AuthenticationCredentials,
  AuthObjectFormat,
  LoginResponse,
  LoginResponseData,
  LoginResponseOrigin,
  UserResponseData
} from 'Douleutaras/interfaces/auth';
import { getNow, updateStoredUser } from 'Douleutaras/utilities/localStorage';
import { RootState } from 'Douleutaras/redux/store';
import { User, UserType } from 'Douleutaras/types';
import {
  RequestPasswordReset,
  RequestPasswordResetResponse,
  RequestResetPasswordConfirm, ResponseV2,
  SocialAuthData,
  UserAuthData,
  UserSignUpResponse
} from 'Douleutaras/redux/data/auth/types';
import { Dictionary } from 'lodash';
import { URLS } from 'Douleutaras/constants/urls';
import { normalizeResponseV2 } from 'Douleutaras/redux/data/auth/helpers';

const LOCAL_STORAGE_AUTH_KEY = 'auth-credentials';
const API_USER = 'api/public/user';

const setAuthData = createAsyncThunk(
  'user/set_auth_data',
  async (userData: LoginResponseOrigin, api) => {
    const v: {
      user: User;
      token: string;
      expires: string;
    } = {
      user: undefined,
      token: undefined,
      expires: undefined
    };

    if (userData.user) {
      v['user'] = userData.user;
    }
    if (userData.token) {
      v['token'] = userData.token;
    }
    if (userData.expires) {
      v['expires'] = userData.expires;
    }
    const authObjectFormat: AuthObjectFormat = {
      t: getNow(),
      v
    };

    // Set in local storage
    localStorage.setItem(LOCAL_STORAGE_KEY.USER_CREDENTIALS, JSON.stringify(authObjectFormat));

    // Save auth credentials to local storage
    localStorage.setItem(LOCAL_STORAGE_AUTH_KEY, JSON.stringify(v));

    // Persist JWT in a cookie so the SSR server can read it on the next request
    const expiry = v.expires ? `; expires=${new Date(v.expires).toUTCString()}` : '';
    const isSecure = window.location.protocol === 'https:';
    document.cookie = `jwt=${v.token}; path=/; SameSite=Lax${isSecure ? '; Secure' : ''}${expiry}`;

    return v;
  }
);

export const clearUserAndRedirect = async (dispatch, next = '') => {
  await dispatch(clearUser());
  if (typeof window !== 'undefined') {
    window.location.href = next ? `${URLS.AUTHENTICATION.LOGIN}/?next=${next}` : URLS.AUTHENTICATION.LOGIN;
  }
};

export const refreshUser = createAsyncThunk<ResponseV2<LoginResponseOrigin>, { token: string }>(
  'user/refresh',
  async (body, api) => {
    const response = await makeRequest('api/public/api-token-refresh', {
      method: 'POST',
      body
    })(api);

    const normalized = normalizeResponseV2<LoginResponseOrigin>(response);

    if (normalized.result === 'success') {
      api.dispatch(setAuthData(response.data));
    }

    return normalized;
  }
);

export const authenticateUser = createAsyncThunk<ResponseV2<UserAuthData>, AuthenticationCredentials>(
  'user/authentication',
  async (credentials: AuthenticationCredentials, api) => {
    const { username, password } = credentials;
    const response: LoginResponseData = await makeRequest(
      'api/public/api-token-auth/',
      {
        method: 'POST',
        body: { username, password }
      })(api);

    const normalized = normalizeResponseV2<UserAuthData>(response);

    if (normalized.result === 'success') {
      api.dispatch(setAuthData(response.data));
    }

    return normalized;
  }
);

export const createUser = createAsyncThunk<ResponseV2<User>, User>(
  'user/create',
  async (data: Partial<User>, api) => {
    const response = await makeRequest(
      API_USER,
      {
        method: 'POST',
        body: data
      })(api);

    return normalizeResponseV2<User>(response);
  }
);

export const createAndAuthenticate = createAsyncThunk(
  'user/createAndAuthenticate',
  async (data: Partial<User>, api) => {
    const response = await api.dispatch(createUser(data))
      .unwrap();

    if (response?.data !== undefined) {
      await api.dispatch(setAuthData({
        token: response.data.jwt.token,
        expires: response.data.jwt.expires,
        user: response.data
      }));
    }

    return api.dispatch(getUser({ id: response?.data.id }));
  }
);

export const getUser = createAsyncThunk(
  'auth/get',
  async ({ id, updateUsageStorage = true }: { id: number, updateUsageStorage?: boolean }, api) => {
    const response = await makeRequest(
  `${API_USER}/${id}/`,
      { method: 'GET' }
    )(api);

    const userData = response.data;
    if (updateUsageStorage) {
      updateStoredUser(userData);
    }

    return userData;
  }
);

export const patchUser = createAsyncThunk(
  'user/patch',
  async ({ id, body }: { id: number; body: Dictionary<unknown> }, api) => {
    const response: UserResponseData = await makeRequest(`${API_USER}/${id}/`, {
      method: 'PATCH',
      body
    })(api);

    const userData = response.data;
    updateStoredUser(userData);

    return userData;
  }
);

export const setUserType = createAsyncThunk(
  'user/changeType',
  async ({ id, type = UserType.SELLER }: { id: number; type?: UserType }, api) => {
    const result = await api.dispatch(patchUser({ id, body: { user_type: type } }));

    if (patchUser.rejected.match(result)) {
      throw result.error;
    }

    return result.payload as User;
  }
);

export const userLogout = createAsyncThunk(
  'user/logout',
  async ({ id }: { id: number }, api) => {
    await makeRequest(
      `api/public/login/${id}/`,
      {
        method: 'DELETE'
      }
    )(api);

    await clearUserAndRedirect(api.dispatch);
  }
);

export const loginCheckUser = createAsyncThunk(
  'user/check',
  async ({ data }: { data: { email?: string; phone?: string } }, api) => {
    const response = await makeRequest('api/public/login/check_user', {
      method: 'POST',
      body: data
    })(api);

    return normalizeResponseV2(response);
  }
);

export const resetPasswordConfirm = createAsyncThunk<ResponseV2<void>, RequestResetPasswordConfirm>(
  'login/resetPasswordConfirm',
  async (data, api) => {
    const response = await makeRequest('api/public/login/reset_password_confirm', {
      method: 'POST',
      body: data,
      configure: {
        withCredentials: false
      }
    })(api);

    return normalizeResponseV2(response);
  }
);

export const requestPasswordReset = createAsyncThunk<RequestPasswordResetResponse, RequestPasswordReset>(
  'user/requestPasswordReset',
  async (data, api) => {
    const response = await makeRequest('api/public/login/reset_password', {
      method: 'POST',
      body: data
    })(api);

    const originalEmail = data.email;

    if (response.data) {
      return {
        ...response.data,
        originalEmail
      };
    }

    if (response.payload) {
      return {
        ...response.payload.response?.data,
        originalEmail
      };
    }

    return {};
  }
);

export const verifySocialAuthEmail = createAsyncThunk<ResponseV2<void>, Dictionary<string>>(
  'login/verifySocialAuth',
  async (data, api) => {
    const res = await makeRequest(`${API_USER}/verify_oauth`, {
      method: 'POST',
      body: data
    })(api);

    return normalizeResponseV2(res);
  }
);

export const socialLogin = createAsyncThunk<ResponseV2<UserAuthData>, SocialAuthData>(
  'auth/social/login',
  async (data, api) => {
    const response = await makeRequest('api/public/api-oauth', {
      method: 'POST',
      body: data
    })(api);

    const normalized = normalizeResponseV2<UserAuthData>(response);

    if (normalized.result === 'success') {
      api.dispatch(setAuthData(response.data));
    }

    return normalized;
  }
);

export const socialRegister = createAsyncThunk<ResponseV2<UserSignUpResponse>, SocialAuthData>(
  'auth/social/register',
  async (data, api) => {
    const response = await makeRequest(`${API_USER}/create_oauth`, {
      method: 'POST',
      body: data
    })(api);

    const normalized = normalizeResponseV2<UserSignUpResponse>(response);

    if (normalized.result === 'success') {
      api.dispatch(setAuthData({
        token: response.data.jwt.token,
        expires: response.data.jwt.expires,
        user: response.data
      }));
    }

    return normalized;
  }
);

export const authSlice = createSlice({
  name: 'auth',
  initialState: {
    isTempUser: false,
    lastVerifyEmail: {}
  } as LoginResponse,
  reducers: {
    setJwtToken(state, { payload }): void {
      state.token = payload;
    },

    setUser(state, { payload }): void {
      state.user = payload;
    },

    setTempUser(state, { payload }): void {
      state.isTempUser = payload;
    },

    clearUser(state): void {
      state.isTempUser = undefined;
      state.user = undefined;
      state.token = undefined;
      state.expires = undefined;
      localStorage.removeItem(LOCAL_STORAGE_AUTH_KEY);
      localStorage.removeItem(LOCAL_STORAGE_KEY.USER_CREDENTIALS);
      const isSecure = window.location.protocol === 'https:';
      document.cookie = `jwt=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax${isSecure ? '; Secure' : ''}`;
    }
  },
  extraReducers: builder => {
    builder.addCase(setAuthData.fulfilled, (state, action) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.expires = action.payload.expires;
      state.isTempUser = false;
    });
    builder.addCase(getUser.fulfilled, (state, action) => {
      state.user = action.payload;
      state.loading = false;
    });
    builder.addCase(patchUser.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(patchUser.fulfilled, (state, action) => {
      state.user = action.payload;
      state.loading = false;
    });
    builder.addCase(patchUser.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(userLogout.fulfilled, () => {});
    builder.addCase(requestPasswordReset.fulfilled, (state, action) => {
      state.lastVerifyEmail = {
        ...action.payload,
        created: new Date().getTime()
      };
    });
  }

});

export const { setJwtToken, setUser, clearUser, setTempUser } = authSlice.actions;
export const selectCurrentUser = (state: RootState) => state.auth.user as User;
export const selectAuthState = (state: RootState) => state.auth;
export const selectIsCreditUser = (state: RootState) => state.auth.user
  && state.auth.user.categories?.some(category => category.credits_model);
export const selectLastVerifiedEmail = (state: RootState) => state.auth.lastVerifyEmail;
export default authSlice.reducer;
